import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  ImageList,
  ImageListItem,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import 'yet-another-react-lightbox/styles.css';
import PageHeader from '../components/PageHeader';
import { motion } from 'framer-motion';
import {
  selectGalleryError,
  selectGalleryStatus,
  selectGalleryItems,
} from '../redux/gallerySlice';

function Gallery() {
  const theme = useTheme();
  const navigate = useNavigate();
  const error = useSelector(selectGalleryError);
  const status = useSelector(selectGalleryStatus);
  const galleryItems = useSelector(selectGalleryItems);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));

  const loading = status === 'loading' || status === 'idle';

  const getColumns = () => {
    if (isMobile) return 1;
    if (isMedium) return 2;
    if (isLarge) return 4;
    return 3;
  };

  const getGap = () => {
    if (isMobile) return 10;
    if (isMedium) return 15;
    if (isLarge) return 20;
    return 30;
  };

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', position: 'relative' }}>
      <PageHeader
        title="Visual Chronicles"
        backgroundImage="/images/headers/gallery_bg.jpeg"
        subtitle={'Immerse yourself in the high-octane legacy of global Gymkhana championships.'}
      />

      <Container maxWidth="xl" sx={{ py: 12, position: 'relative', zIndex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#4f46e5' }} />
          </Box>
        ) : error ? (
          <Box sx={{ py: 4 }}>
            <Alert severity="error" sx={{ borderRadius: 4 }}>{error}</Alert>
          </Box>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <ImageList
              gap={getGap()}
              variant="quilted"
              cols={getColumns()}
              sx={{ overflow: 'visible' }}
            >
              {(galleryItems || []).map((folder) => (
                <ImageListItem
                  key={folder.id}
                  onClick={() => { navigate(`/gallery-details/${folder.id}`); }}
                  sx={{
                    overflow: 'hidden',
                    borderRadius: '24px',
                    position: 'relative',
                    cursor: 'pointer',
                    bgcolor: '#0f172a', // Obsidian Background
                    transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                    height: { xs: '350px !important', md: '370px !important' },
                    boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: '0 40px 80px rgba(79, 70, 229, 0.15)',
                      zIndex: 2,
                      '& img': { transform: 'scale(1.1)', opacity: 0.85 },
                      '& .overlay-box': { bgcolor: 'rgba(0, 0, 0, 0.2)' }
                    }
                  }}
                >


                  <motion.img
                    src={folder.coverImage}
                    alt={folder.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      opacity: 0.95, // Let background bleed through slightly
                      transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)'
                    }}
                  />
                  <Box
                    className="content-overlay"
                    sx={{
                      left: 0,
                      bottom: 0,
                      top: 0,
                      right: 0,
                      width: '100%',
                      height: '100%',
                      position: 'absolute',
                      background: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                    }}
                  >
                    {/* Floating Corner Tag */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 24,
                        right: 24,
                        bgcolor: 'rgba(15, 23, 42, 0.9)',
                        backdropFilter: 'blur(8px)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.1)',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4f46e5', boxShadow: '0 0 8px #4f46e5' }} />
                      <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 900, letterSpacing: 1.5 }}>
                        {folder.images?.length || 0} ASSETS
                      </Typography>
                    </Box>

                    {/* High-Contrast Integrated Bottom Gradient */}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', p: 4, transition: 'all 0.4s ease', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: '160px' }}>
                      <Typography
                        variant="h4"
                        sx={{
                          color: '#ffffff',
                          fontFamily: 'Bebas Neue',
                          fontSize: '2.1rem',
                          lineHeight: 1,
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {folder.title || folder.folderName}
                      </Typography>
                    </Box>
                  </Box>
                </ImageListItem>
              ))}
            </ImageList>
          </motion.div>
        )}
      </Container>
    </Box>
  );
}

export default Gallery;
